import { Progress } from "antd";
import {
  DatabaseOutlined,
  RobotOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  PictureOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { SvgConnections } from "./CompareOverview";
import type { CompareRun } from "../lib/align";

export const SmartCardCompareView = ({
  runs,
  absoluteConfigs,
  diffOnly,
}: {
  runs: CompareRun[];
  absoluteConfigs: any[];
  diffOnly: boolean;
}) => {
  const isIdentical = (source: "params" | "judge", key: string) => {
    const vals = absoluteConfigs.map((c) => c[source][key]);
    const unique = new Set(vals);
    return unique.size <= 1;
  };

  const getOpacity = (source: "params" | "judge", key: string) => {
    if (!diffOnly) return "opacity-100";
    return isIdentical(source, key) ? "opacity-30 grayscale" : "opacity-100";
  };

  const _MutedDefault = () => (
    <span className="text-gray-400 font-medium text-xs bg-gray-100/50 px-2 py-1 rounded-md inline-block">
      Not Configured
    </span>
  );

  return (
    <div className="relative p-6 bg-slate-50/50 rounded-b-xl overflow-hidden">
      <SvgConnections runs={runs} />

      <div
        className="grid gap-6 relative z-10"
        style={{
          gridTemplateColumns: `repeat(${runs.length}, minmax(0, 1fr))`,
        }}
      >
        {runs.map((r, idx) => {
          const config = absoluteConfigs.find((c) => c.id === r.id);
          if (!config) return null;
          const p = config.params;
          const j = config.judge;

          const themeColor = r.color || (idx === 0 ? "#3b82f6" : "#22c55e");
          const bgTint = `${themeColor}10`;
          const borderTint = `${themeColor}40`;
          const iconBgTint = `${themeColor}20`;

          return (
            <div
              key={r.id}
              className="rounded-2xl overflow-hidden flex flex-col transition-all shadow-sm border"
              style={{ borderColor: borderTint, backgroundColor: bgTint }}
            >
              {/* Card Header */}
              <div
                className="px-4 py-2.5 flex items-center gap-2 text-white font-semibold text-sm justify-center shadow-sm relative overflow-hidden"
                style={{ backgroundColor: themeColor }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent pointer-events-none" />
                <span className="w-2.5 h-2.5 rounded-full bg-white/80 inline-block shadow-sm z-10" />
                <span className="z-10">{r.label}</span>
              </div>

              {/* Card Body */}
              <div className="p-4 flex-1 flex flex-col gap-3 relative">
                {/* Row 1: Provider & Model */}
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`bg-white rounded-xl p-2.5 border shadow-sm flex items-center gap-3 transition-opacity duration-300 ${getOpacity("params", "provider")}`}
                    style={{ borderColor: borderTint }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0"
                      style={{ backgroundColor: iconBgTint, color: themeColor }}
                    >
                      <DatabaseOutlined />
                    </div>
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-800">
                        Provider
                      </span>
                      <div
                        className="text-xs font-medium text-gray-500 truncate mt-0.5"
                        title={p.provider}
                      >
                        {p.provider || "Not Configured"}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`bg-white rounded-xl p-2.5 border shadow-sm flex items-center gap-3 transition-opacity duration-300 ${getOpacity("params", "model")}`}
                    style={{ borderColor: borderTint }}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0"
                      style={{ backgroundColor: iconBgTint, color: themeColor }}
                    >
                      <RobotOutlined />
                    </div>
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-800">
                        Model
                      </span>
                      <div
                        className="text-xs font-medium text-gray-500 truncate mt-0.5"
                        title={p.model}
                      >
                        {p.model || "Not Configured"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Temperature & Tokens */}
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`bg-white rounded-xl p-3 border shadow-sm flex items-center transition-opacity duration-300 ${getOpacity("params", "temperature")}`}
                    style={{ borderColor: borderTint }}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <DashboardOutlined
                        className="text-lg"
                        style={{ color: themeColor }}
                      />
                      <div className="flex flex-col">
                        <span className="text-[11px] font-bold tracking-wider text-gray-800 mb-1">
                          Temperature
                        </span>
                        <div
                          className="bg-indigo-50 text-indigo-700 font-bold px-3 py-1 rounded-lg w-fit text-sm shadow-sm"
                          style={{
                            backgroundColor: iconBgTint,
                            color: themeColor,
                          }}
                        >
                          {p.temperature ?? "—"}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center justify-center relative w-14 h-14">
                      {p.temperature != null ? (
                        <>
                          <Progress
                            type="circle"
                            percent={(p.temperature / 2.0) * 100}
                            size={56}
                            strokeColor={themeColor}
                            trailColor="#f1f5f9"
                            showInfo={false}
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-800">
                            {p.temperature}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-300 font-bold text-xl">
                          —
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div
                      className={`bg-white rounded-xl p-2.5 border shadow-sm flex flex-col flex-1 justify-center transition-opacity duration-300 ${getOpacity("params", "topP")}`}
                      style={{ borderColor: borderTint }}
                    >
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="text-[11px] font-bold tracking-wider text-gray-800">
                          Top-P
                        </span>
                        <div
                          className="bg-indigo-50 text-indigo-700 font-bold px-2 py-0.5 rounded text-xs shadow-sm"
                          style={{
                            backgroundColor: iconBgTint,
                            color: themeColor,
                          }}
                        >
                          {p.topP ?? "—"}
                        </div>
                      </div>
                      {p.topP != null ? (
                        <Progress
                          percent={p.topP * 100}
                          showInfo={false}
                          size={["100%", 6]}
                          strokeColor={themeColor}
                          trailColor="#f1f5f9"
                          className="m-0"
                        />
                      ) : (
                        <div className="h-1.5 w-full bg-gray-100 rounded-full" />
                      )}
                    </div>
                    <div
                      className={`bg-white rounded-xl p-2.5 border shadow-sm flex flex-col flex-1 justify-center transition-opacity duration-300 ${getOpacity("params", "maxTokensPerSession")}`}
                      style={{ borderColor: borderTint }}
                    >
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <AppstoreOutlined style={{ color: themeColor }} />
                          <span className="text-[11px] font-bold tracking-wider text-gray-800">
                            Max Tokens
                          </span>
                        </div>
                        <span className="text-xs font-bold text-gray-600">
                          {p.maxTokensPerSession ?? "—"}
                        </span>
                      </div>
                      {p.maxTokensPerSession != null ? (
                        <Progress
                          percent={Math.min(
                            100,
                            (p.maxTokensPerSession / 128000) * 100,
                          )}
                          showInfo={false}
                          size={["100%", 6]}
                          strokeColor={themeColor}
                          trailColor="#f1f5f9"
                          className="m-0"
                        />
                      ) : (
                        <div className="h-1.5 w-full bg-gray-100 rounded-full" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 4: Judge */}
                <div
                  className="rounded-xl p-3 border transition-opacity duration-300 mt-1"
                  style={{
                    backgroundColor: `${themeColor}10`,
                    borderColor: borderTint,
                  }}
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5 text-gray-600">
                    JUDGE SETTINGS
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div
                      className={`bg-white rounded-xl p-2.5 border border-white shadow-sm flex flex-col items-center justify-center transition-opacity duration-300 ${getOpacity("judge", "enabled")}`}
                    >
                      <span className="text-[10px] font-bold text-gray-800 mb-2 whitespace-nowrap">
                        Judge Enabled
                      </span>
                      {j.enabled ? (
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-[0_0_12px_rgba(34,197,94,0.6)]">
                            <div className="w-2 h-2 bg-gradient-to-br from-white to-green-100 rounded-full" />
                          </div>
                          <span className="text-[10px] font-bold text-green-600 mt-1">
                            Yes
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                            <div className="w-2 h-2 bg-gradient-to-br from-white to-gray-100 rounded-full" />
                          </div>
                          <span className="text-[10px] font-bold text-gray-500 mt-1">
                            No
                          </span>
                        </div>
                      )}
                    </div>

                    <div
                      className={`bg-white rounded-xl p-2.5 border border-white shadow-sm flex flex-col items-center justify-center transition-opacity duration-300 ${getOpacity("judge", "mode")}`}
                    >
                      <span className="text-[10px] font-bold text-gray-800 mb-2 whitespace-nowrap">
                        Judge Mode
                      </span>
                      {j.mode ? (
                        <div
                          className="flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 text-[10px] font-bold"
                          style={{
                            color: themeColor,
                            backgroundColor: iconBgTint,
                          }}
                        >
                          {j.mode === "image" ? (
                            <PictureOutlined />
                          ) : (
                            <FileTextOutlined />
                          )}
                          <span>{j.mode}</span>
                          {j.mode === "image" && (
                            <span className="text-xs">🖼️</span>
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-8 bg-gray-50 rounded-lg" />
                      )}
                    </div>

                    <div
                      className={`bg-white rounded-xl p-2.5 border border-white shadow-sm flex flex-col items-center justify-center transition-opacity duration-300 ${getOpacity("judge", "model")}`}
                    >
                      <span className="text-[10px] font-bold text-gray-800 mb-2 whitespace-nowrap">
                        Judge Model
                      </span>
                      {j.model ? (
                        <span
                          className="text-[10px] font-semibold text-center text-gray-600 break-all line-clamp-2 leading-tight"
                          title={j.model}
                        >
                          {j.model}
                        </span>
                      ) : (
                        <div className="w-full h-8 bg-gray-50 rounded-lg" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
